import { useState } from 'react';
import { Matcher } from 'react-day-picker';
import { ControllerRenderProps } from 'react-hook-form';

import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { CalendarIcon } from 'lucide-react';

import { Button } from 'components/Button';
import { FieldAttributes } from 'types/LoadAttributes';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { cn } from 'utils/shadcn';

import { Calendar } from './Calendar';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Returns a date adjusted for calendar display in the given timezone.
 * When tz is 'UTC', returns "UTC date at local midnight" so that react-day-picker
 * (which uses local time) highlights the same day as the UTC-formatted label.
 * Exported for unit tests.
 */
export function getTimezoneAdjustedDate(
  date: Date,
  tz: MaybeUndef<string>
): Date {
  if (!tz) return date;

  if (tz === 'UTC') {
    return new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      0,
      0,
      0
    );
  }

  const dateInTz = dayjs(date).tz(tz);
  return dayjs()
    .tz(tz)
    .year(dateInTz.year())
    .month(dateInTz.month())
    .date(dateInTz.date())
    .toDate();
}

export interface DatePickerProps {
  field?: ControllerRenderProps<any>;
  thisFieldAttr?: FieldAttributes;
  highlightDirtyField?: boolean;
  highlightError?: boolean;
  logProperties?: Record<string, any>;
  className?: string;
  calendarClassName?: string;
  disabled?: Matcher | Matcher[];
  timezone?: MaybeUndef<string>;
  onChange?: (date: Maybe<Date | Dayjs>) => void;
  readOnly?: boolean;
}

function DatePicker({
  field,
  highlightDirtyField,
  highlightError,
  thisFieldAttr,
  logProperties,
  className,
  calendarClassName,
  disabled,
  timezone,
  onChange,
  readOnly = false,
}: DatePickerProps) {
  const [isCalendarPopoverOpen, setIsCalendarPopoverOpen] = useState(false);

  const textPlaceholder =
    thisFieldAttr?.isReadOnly && !field?.value ? '' : 'Pick a date';

  const fieldValueToDate = (value: any): Maybe<Date> => {
    if (!value) return null;
    return dayjs.isDayjs(value) ? value.toDate() : new Date(value as string);
  };

  const formatDisplayDate = (
    date: Maybe<Date>,
    tz: MaybeUndef<string>
  ): string => {
    if (!date) return textPlaceholder;
    if (tz) {
      return dayjs(date).tz(tz).format('MM/DD/YY');
    }
    return dayjs(date).format('MM/DD/YY');
  };

  const currentDate = fieldValueToDate(field?.value);
  // Empty: open on today's month. Selected: open on that date's month.
  const calendarDate = currentDate
    ? getTimezoneAdjustedDate(currentDate, timezone)
    : new Date();

  return (
    <Popover
      open={isCalendarPopoverOpen}
      onOpenChange={(open) => setIsCalendarPopoverOpen(open)}
    >
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          size='sm'
          className={cn(
            'w-full justify-start text-left font-normal rounded-sm border-neutral-input-border bg-neutral-input-bg text-neutral-input-text py-1 h-8',
            'disabled:cursor-not-allowed disabled:bg-neutral-input-disabled-bg disabled:border-neutral-input-disabled-border disabled:opacity-100',
            !field?.value && 'text-neutral-400',
            highlightDirtyField && 'bg-warning-200',
            highlightError && 'bg-error-50 border-error-500',
            className
          )}
          buttonNamePosthog={ButtonNamePosthog.ToggleDatePicker}
          logProperties={logProperties}
          disabled={readOnly || thisFieldAttr?.isReadOnly}
        >
          <CalendarIcon
            className={cn(
              'mr-2 h-4 w-4 shrink-0 stroke-neutral-500',
              calendarClassName
            )}
          />
          <span className='text-[13px] text-neutral-500'>
            {formatDisplayDate(currentDate, timezone)}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-auto p-0'>
        {/* Remount on open so defaultMonth always reflects today vs selected. */}
        <Calendar
          key={
            isCalendarPopoverOpen
              ? currentDate
                ? String(currentDate.getTime())
                : 'today'
              : 'closed'
          }
          mode='single'
          required
          defaultMonth={calendarDate}
          selected={currentDate ? calendarDate : undefined}
          onSelect={(newValue: Undef<Date>) => {
            if (typeof newValue === 'undefined') {
              field?.onChange(null);
              onChange?.(null);
              return;
            }
            let dateToSet: Date;

            if (timezone) {
              const year = newValue.getFullYear();
              const month = newValue.getMonth() + 1; // JS months are 0-indexed
              const day = newValue.getDate();

              if (currentDate) {
                // Preserve existing time in the target timezone
                const existingDateTime = dayjs(currentDate).tz(timezone);
                dateToSet = dayjs
                  .tz(`${year}-${month}-${day}`, 'YYYY-M-D', timezone)
                  .hour(existingDateTime.hour())
                  .minute(existingDateTime.minute())
                  .toDate();
              } else {
                dateToSet = dayjs
                  .tz(`${year}-${month}-${day}`, 'YYYY-M-D', timezone)
                  .startOf('day')
                  .toDate();
              }
            } else {
              // Browser local when no timezone — legacy date-only callers
              if (currentDate && newValue) {
                newValue.setHours(currentDate.getHours());
                newValue.setMinutes(currentDate.getMinutes());
              }
              dateToSet = newValue;
            }

            field?.onChange(dateToSet);
            onChange?.(dateToSet);
            setIsCalendarPopoverOpen(false);
          }}
          disabled={disabled}
        />
      </PopoverContent>
    </Popover>
  );
}

export { DatePicker };
